import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '../composables/useAuth'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/LoginPage.vue'),
    meta: {
      title: '用户登录',
      requiresAuth: false
    }
  },
  
  {
    path: '/',
    name: 'Home',
    redirect: '/stations',
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/stations',
    name: 'Stations',
    component: () => import('../views/StationManager.vue'),
    meta: {
      title: '车站管理',
      icon: 'LocationInformation',
      requiresAuth: true,
      allowGuest: true,
      requiredPermissions: ['tab.station_manager.view']
    }
  },
  {
    path: '/lines',
    name: 'Lines',
    component: () => import('../views/LineManager.vue'),
    meta: {
      title: '线路管理',
      icon: 'Connection',
      requiresAuth: true,
      allowGuest: true,
      requiredPermissions: ['tab.line_manager.view']
    }
  },
  {
    path: '/operators',
    name: 'Operators',
    component: () => import('../views/OperatorDirectory.vue'),
    meta: {
      title: '运营方管理',
      icon: 'OfficeBuilding',
      requiresAuth: true,
      allowGuest: true,
      requiredPermissions: ['tab.operators.view']
    }
  },
  {
    path: '/buildings',
    name: 'Buildings',
    component: () => import('../views/BuildingManager.vue'),
    meta: {
      title: '建筑管理',
      icon: 'School',
      requiresAuth: true,
      allowGuest: true,
      requiredPermissions: ['tab.buildings.view']
    }
  },
  {
    path: '/line-info',
    name: 'LineInfo',
    component: () => import('../views/LineInfo.vue'),
    meta: {
      title: '线路信息',
      icon: 'Document',
      requiresAuth: true,
      allowGuest: true,
      requiredPermissions: ['tab.line_info.view']
    }
  },
  {
    path: '/tiled-map',
    name: 'TiledMap',
    component: () => import('../views/TiledMapViewer.vue'),
    meta: {
      title: '切片地图',
      icon: 'Grid',
      requiresAuth: true,
      allowGuest: true,
      fullscreen: true,
      requiredPermissions: ['tab.tiled_map.view']
    }
  },
  {
    path: '/bluemap',
    name: 'BlueMap',
    component: () => import('../views/BlueMap.vue'),
    meta: {
      title: 'BlueMap 地图',
      icon: 'MapLocation',
      requiresAuth: true,
      allowGuest: true,
      fullscreen: true,
      new: true,
      requiredPermissions: ['tab.bluemap.view']
    }
  },
  {
    path: '/map-view',
    name: 'MapView',
    component: () => import('../views/MapView.vue'),
    meta: {
      title: '地图展示',
      icon: 'MapLocation',
      requiresAuth: true,
      allowGuest: true,
      requiredPermissions: ['tab.map_view.view']
    }
  },
  {
    path: '/navigation',
    name: 'StationNavigation',
    component: () => import('../views/StationNavigator.vue'),
    meta: {
      title: '车站导航',
      icon: 'Guide',
      requiresAuth: true,
      allowGuest: true,
      requiredPermissions: ['tab.navigation.view']
    }
  },
  {
    path: '/export',
    name: 'Export',
    component: () => import('../views/ExportManager.vue'),
    meta: {
      title: '数据导出',
      icon: 'Download',
      requiresAuth: true,
      requiredPermissions: ['tab.export.view']
    }
  },
  {
    path: '/resource-packs',
    name: 'ResourcePacks',
    component: () => import('../views/ResourcePackManager.vue'),
    meta: {
      title: '资源包管理',
      icon: 'CollectionTag',
      requiresAuth: true,
      requiredPermissions: ['tab.resource_packs.view', 'resource_pack.read']
    }
  },
  {
    path: '/stats',
    name: 'Stats',
    component: () => import('../views/StatsView.vue'),
    meta: {
      title: '统计分析',
      icon: 'DataAnalysis',
      requiresAuth: true,
      requiredPermissions: ['tab.stats.view']
    }
  },
  {
    path: '/audit',
    name: 'AuditLogs',
    component: () => import('../views/AuditLogView.vue'),
    meta: {
      title: '编辑历史',
      icon: 'Document',
      requiresAuth: true,
      requiredPermissions: ['tab.audit.view']
    }
  },
  {
    path: '/access',
    name: 'AccessControl',
    component: () => import('../views/AccessControl.vue'),
    meta: {
      title: '权限管理',
      icon: 'Lock',
      requiresAuth: true,
      requiredPermissions: ['admin.access.manage']
    }
  },
  {
    path: '/timetable',
    name: 'Timetable',
    component: () => import('../views/TimetableArchitect.vue'),
    meta: {
      title: '交路设置',
      icon: 'Histogram',
      requiresAuth: true,
      allowGuest: true,
      fullscreen: true,
      requiredPermissions: ['tab.timetable.view']
    }
  },
  {
    path: '/assistant',
    name: 'AiAssistant',
    component: () => import('../views/AiAssistant.vue'),
    meta: {
      title: 'AI 助手',
      icon: 'ChatDotRound',
      requiresAuth: true,
      allowGuest: true
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - ROFC车站管理系统`
  }
  
  // 检查是否需要认证
  if (to.meta.requiresAuth !== false) {
    const { isAuthenticated, initAuth, isGuest, hasPermission } = useAuth()
    
    // 初始化认证状态
    await initAuth()
    
    if (isGuest.value) {
      if (to.meta.allowGuest) {
        next()
        return
      }
      next('/lines')
      return
    }

    if (!isAuthenticated.value) {
      next('/login')
      return
    }

    if (to.meta?.requiredPermissions && !hasPermission(to.meta.requiredPermissions)) {
      // 无权限，跳转登录或拒绝
      if (to.path === '/login') {
        next(false)
        return
      }
      next('/login')
      return
    }
  } else if (to.path === '/login') {
    // 如果已经登录，访问登录页时重定向到首页
    const { isAuthenticated, initAuth, isGuest } = useAuth()
    initAuth()
    
    if (isGuest.value) {
      next('/lines')
      return
    }
    
    if (isAuthenticated.value) {
      next('/')
      return
    }
  }
  
  next()
})

export default router
